'use client';

import { createContext, type ReactNode, useContext, useRef, useSyncExternalStore } from 'react';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { createViduStore, type ViduStore } from '@/app/stores';

export type StoreApi = ReturnType<typeof createViduStore>;

export const StoreContext = createContext<StoreApi | undefined>(undefined);

export interface StoreProviderProps {
  children: ReactNode;
}

export const StoreProvider = ({ children }: StoreProviderProps) => {
  const storeRef = useRef<StoreApi | undefined>(undefined);
  if (!storeRef.current) {
    storeRef.current = createViduStore();
  }

  return <StoreContext.Provider value={storeRef.current}>{children}</StoreContext.Provider>;
};

/**
 * @description 用于组件内订阅状态变化的 Hook
 * @template T 返回状态类型
 * @param selector 状态选择函数（将触发组件重新渲染）
 * @param equalityFn 可选的状态比较函数
 * @returns 当前选中状态的最新值（与 React 渲染周期同步）
 *
 * 使用场景：
 * - 需要在组件中响应状态变化时
 * - 需要自动处理订阅/取消订阅时
 *
 * 注意事项：
 * - 返回的是 React 渲染周期的状态快照
 */
export const useStore = <T,>(
  selector: (state: ViduStore) => T,
  equalityFn: (left: T, right: T) => boolean = shallow,
) => {
  const store = useContext(StoreContext);

  if (!store) {
    throw new Error('useStore must be used within StoreProvider');
  }

  return useStoreWithEqualityFn(store, selector, equalityFn);
};

const EMPTY_SUBSCRIBE = () => () => {};
const EMPTY_SNAPSHOT = undefined;

/** 在可选挂载的共享组件中读取 store；没有 StoreProvider 时返回 undefined。 */
export const useStoreOptional = <T,>(selector: (state: ViduStore) => T): T | undefined => {
  const store = useContext(StoreContext);
  return useSyncExternalStore(
    store?.subscribe ?? EMPTY_SUBSCRIBE,
    store ? () => selector(store.getState()) : () => EMPTY_SNAPSHOT,
    () => EMPTY_SNAPSHOT,
  ) as T | undefined;
};

/**
 * @description 获取 Store 原生 API 的 Hook
 * @returns 完整的 Store API 对象（包含 getState/setState/subscribe 等方法）
 *
 * 使用场景：
 * - 需要在事件处理中同步读取最新状态
 * - 需要在非 React 上下文中操作 Store
 * - 需要访问底层 Store 方法时
 *
 * 注意事项：
 * - 直接操作可能绕过 React 更新机制
 */
export const useStoreApi = () => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('useStoreApi must be used within StoreProvider');
  }
  return store;
};
